import { FC } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/lib';
import { RouteConstants } from '@/features/router/RouteConstants';

/** Reason the API returns with its 409 when the Whatsapp Business app has no number attached. */
export const WHATSAPP_NUMBER_NOT_CONFIGURED = 'WHATSAPP_NUMBER_NOT_CONFIGURED';

/**
 * True for the error the API raises when the workspace has a Whatsapp Business account but no
 * phone number on it. Everything downstream — templates, campaigns, sending — depends on that
 * number, so this is not a failure to report but a setup step to offer.
 */
export const isWhatsappNumberNotConfiguredError = (error: unknown): boolean => {
  const candidate = error as { status?: number; data?: { content?: { reason?: string } } } | undefined;
  return candidate?.status === 409 && candidate?.data?.content?.reason === WHATSAPP_NUMBER_NOT_CONFIGURED;
};

/**
 * Whatsapp Business is connected but no phone number is attached yet. Nothing in the workspace can
 * work until the customer opens Meta's embedded signup, so that link is the only thing on screen.
 */
export const WhatsappBusinessOnboardingBlock: FC<{ onboardingUrl?: string }> = ({ onboardingUrl }) => {
  const history = useHistory();
  const { t } = useTranslation('campaign');

  return (
    <div className='flex flex-col items-center justify-center gap-6 rounded-lg border border-gray-400 py-20'>
      <h2 className='text-4xl font-semibold text-slate-600'>{t('whatsappWorkspace.onboardingTitle')}</h2>
      <div className='flex max-w-2xl flex-col items-center gap-4 text-center'>{t('whatsappWorkspace.onboardingDescription')}</div>
      <FaWhatsapp className='size-12 text-emerald-500' />
      {onboardingUrl ? (
        <>
          <Button onClick={() => window.open(onboardingUrl, '_blank', 'noopener,noreferrer')}>{t('whatsappWorkspace.onboardingCta')}</Button>
          {/* The signed link expires five days after it is minted, and there is no way to tell an
              expired one apart from a live one before opening it. */}
          <span className='text-sm text-gray-500'>{t('whatsappWorkspace.onboardingLinkExpiry')}</span>
        </>
      ) : (
        <>
          <div className='text-center text-gray-500'>{t('whatsappWorkspace.onboardingLinkMissing')}</div>
          <Button onClick={() => history.push(RouteConstants.destinations)}>{t('goToDestinationsText')}</Button>
        </>
      )}
    </div>
  );
};
